import React, { useState, useEffect, useRef } from 'react';
import {
  X, Wrench, Clock, Package, User, Phone, Mail, Building2,
  StickyNote, Send, Paperclip, Trash2, Eye, EyeOff, FileText, Film, Image,
  MessageSquare, Printer, CheckCircle, AlertCircle, ChevronDown, ChevronUp
} from 'lucide-react';

const STATUS_COLORS = {
  'Reçu': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'En cours': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Réparé': 'bg-green-500/20 text-green-400 border-green-500/30',
  'Rendu': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Refusé': 'bg-red-500/20 text-red-400 border-red-500/30',
  'En attente pièce': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  'En attente accord': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  'Devis envoyé': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
};

const B2BRepairDetailModal = ({ repair, isOpen, onClose, api, onOpenChat, token }) => {
  const [notes, setNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [noteVisibility, setNoteVisibility] = useState('internal');
  const [noteFiles, setNoteFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  const fileInputRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isOpen && repair?.ref) fetchNotes();
    if (!isOpen) { setNotes([]); setNewNoteText(''); setNoteFiles([]); }
  }, [isOpen, repair?.ref]);

  const fetchNotes = async () => {
    try {
      const res = await api.get(`/repair-notes/${repair.ref}`);
      setNotes(res.data || []);
    } catch { }
  };

  const addNote = async () => {
    if (!newNoteText.trim() && noteFiles.length === 0) return;
    setSubmitting(true);
    try {
      const res = await api.post('/repair-notes', {
        device_ref: repair.ref,
        text: newNoteText.trim() || `${noteFiles.length} fichier(s) joint(s)`,
        visibility: noteVisibility,
      });
      const noteId = res.data?.id;
      if (noteId && noteFiles.length > 0) {
        for (let i = 0; i < noteFiles.length; i++) {
          setUploadProgress(`Upload ${i + 1}/${noteFiles.length}...`);
          const fd = new FormData();
          fd.append('file', noteFiles[i]);
          try {
            await api.post(`/repair-notes/${noteId}/attachments`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000
            });
          } catch (err) { console.error('Upload error:', err); }
        }
      }
      setNewNoteText(''); setNoteFiles([]); setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchNotes();
    } catch (err) { alert(err?.response?.data?.detail || 'Erreur'); }
    setSubmitting(false); setUploadProgress(null);
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    try { await api.delete(`/repair-notes/${noteId}`); fetchNotes(); } catch { }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setNoteFiles(prev => [...prev, ...files]);
  };

  const removeFile = (idx) => setNoteFiles(prev => prev.filter((_, i) => i !== idx));

  if (!isOpen || !repair) return null;

  const statusStyle = STATUS_COLORS[repair.status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  const timeline = repair.timeline || [];
  const photos = repair.photos || [];

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="b2b-repair-detail-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-0 flex items-start sm:items-center justify-center overflow-y-auto p-2 sm:p-4">
        <div className="relative w-full max-w-3xl bg-[#0e0e16] border border-white/10 rounded-2xl shadow-2xl my-4 pointer-events-auto" onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b border-white/10 bg-[#111118] rounded-t-2xl">
            <div className="flex items-center gap-3 min-w-0">
              <Wrench className="w-5 h-5 text-cyan-400 shrink-0" />
              <div className="min-w-0">
                <h2 className="text-base font-bold text-white truncate">{repair.ref}</h2>
                <p className="text-xs text-gray-500">{repair.brand} {repair.model}</p>
              </div>
              <span className={`ml-2 px-2.5 py-1 rounded-lg text-xs font-bold border shrink-0 ${statusStyle}`}>{repair.status}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {onOpenChat && (
                <button onClick={() => { onClose(); onOpenChat(repair); }} className="p-2 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500/30" title="Chat" data-testid="detail-chat-btn">
                  <MessageSquare className="w-4 h-4" />
                </button>
              )}
              <button onClick={onClose} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white" data-testid="detail-close-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="p-5 space-y-5 max-h-[75vh] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>

            {/* Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Company */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Entreprise</h3>
                <p className="text-sm font-bold text-white">{repair.company_name}</p>
                {repair.company_email && <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><Mail className="w-3 h-3" />{repair.company_email}</p>}
                {repair.company_phone && <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1"><Phone className="w-3 h-3" />{repair.company_phone}</p>}
              </div>
              {/* Device */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5" /> Appareil</h3>
                <div className="space-y-1.5 text-sm">
                  <div><span className="text-gray-500">Marque:</span> <span className="text-white font-semibold">{repair.brand}</span></div>
                  <div><span className="text-gray-500">Modele:</span> <span className="text-white font-semibold">{repair.model}</span></div>
                  {repair.imei && <div><span className="text-gray-500">IMEI:</span> <span className="text-gray-200 font-mono text-xs">{repair.imei}</span></div>}
                  {repair.problem && <div><span className="text-gray-500">Probleme:</span> <span className="text-gray-200">{repair.problem}</span></div>}
                  {repair.back_glass_broken && <span className="inline-block mt-1 px-2 py-0.5 bg-red-500/20 text-red-400 border border-red-500/30 rounded text-[10px] font-bold">Vitre arriere cassee</span>}
                </div>
              </div>
            </div>

            {/* Price & Dates */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {repair.price != null && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 text-center">
                  <p className="text-[10px] text-emerald-500 uppercase font-bold">Prix</p>
                  <p className="text-lg font-bold text-emerald-400">{Number(repair.price).toFixed(2)}€</p>
                </div>
              )}
              {repair.parts_price != null && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 text-center">
                  <p className="text-[10px] text-blue-500 uppercase font-bold">Pieces</p>
                  <p className="text-lg font-bold text-blue-400">{Number(repair.parts_price).toFixed(2)}€</p>
                </div>
              )}
              <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
                <p className="text-[10px] text-gray-500 uppercase font-bold">Cree le</p>
                <p className="text-sm font-semibold text-gray-200">{repair.created_at ? new Date(repair.created_at).toLocaleDateString('fr-FR') : '-'}</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
                <p className="text-[10px] text-gray-500 uppercase font-bold">Maj</p>
                <p className="text-sm font-semibold text-gray-200">{repair.updated_at ? new Date(repair.updated_at).toLocaleDateString('fr-FR') : '-'}</p>
              </div>
            </div>

            {/* Photos */}
            {photos.length > 0 && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-1.5"><Image className="w-3.5 h-3.5" /> Photos ({photos.length})</h3>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {photos.map((p, i) => {
                    const photoUrl = typeof p === 'string'
                      ? (p.startsWith('/api') ? `${API_URL}${p}` : `${API_URL}/api/b2b/repairs/${repair.ref}/photos/${p}`)
                      : `${API_URL}/api/b2b/repairs/${repair.ref}/photos/${p.filename}`;
                    return (
                      <a key={i} href={photoUrl} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden border border-white/10 hover:border-cyan-500/50">
                        <img src={photoUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Timeline */}
            {timeline.length > 0 && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <button onClick={() => setShowTimeline(!showTimeline)} className="w-full flex items-center justify-between text-xs font-bold text-gray-500 uppercase" data-testid="toggle-timeline">
                  <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Historique ({timeline.length})</span>
                  {showTimeline ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showTimeline && (
                  <div className="mt-3 space-y-2">
                    {timeline.slice().reverse().map((ev, i) => (
                      <div key={i} className="flex items-start gap-3 text-xs">
                        <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-1.5 shrink-0" />
                        <div>
                          <span className="text-gray-200">{ev.action || ev.status}</span>
                          <span className="text-gray-600 ml-2">{ev.date ? new Date(ev.date).toLocaleString('fr-FR') : ''}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* NOTES Section */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid="b2b-notes-section">
              <h3 className="text-xs font-bold text-gray-500 uppercase mb-3 flex items-center gap-1.5">
                <StickyNote className="w-3.5 h-3.5" /> Notes
              </h3>

              {/* Add note form */}
              <div className="mb-4">
                <textarea
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:border-cyan-500/40 outline-none resize-none"
                  rows={2}
                  placeholder="Ajouter une note..."
                  data-testid="b2b-note-input"
                />

                {/* File previews */}
                {noteFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {noteFiles.map((f, i) => (
                      <div key={i} className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs text-gray-300">
                        {f.type.startsWith('image/') ? <Image className="w-3 h-3 text-blue-400" /> : <FileText className="w-3 h-3 text-gray-400" />}
                        <span className="max-w-[100px] truncate">{f.name}</span>
                        <button onClick={() => removeFile(i)} className="text-red-400 hover:text-red-300"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}

                {uploadProgress && <p className="text-xs text-cyan-400 mt-1">{uploadProgress}</p>}

                <div className="flex items-center justify-between mt-2 gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setNoteVisibility(noteVisibility === 'public' ? 'internal' : 'public')}
                      className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium border transition-colors ${
                        noteVisibility === 'public'
                          ? 'bg-green-500/10 text-green-400 border-green-500/20'
                          : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                      }`}
                      data-testid="b2b-note-visibility"
                    >
                      {noteVisibility === 'public' ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {noteVisibility === 'public' ? 'Public' : 'Interne'}
                    </button>

                    <input type="file" ref={fileInputRef} onChange={handleFileSelect} multiple accept="image/*,video/*,.pdf,.doc,.docx" className="hidden" />
                    <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium border bg-blue-500/10 text-blue-400 border-blue-500/20 hover:bg-blue-500/20" data-testid="b2b-note-attach">
                      <Paperclip className="w-3 h-3" /> Fichiers
                    </button>
                  </div>
                  <button
                    onClick={addNote}
                    disabled={(!newNoteText.trim() && noteFiles.length === 0) || submitting}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-40 bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30"
                    data-testid="b2b-note-submit"
                  >
                    <Send className="w-3 h-3" /> {submitting ? 'Envoi...' : 'Ajouter'}
                  </button>
                </div>
              </div>

              {/* Notes list */}
              {notes.length === 0 ? (
                <p className="text-xs text-gray-600 text-center py-2">Aucune note</p>
              ) : (
                <div className="space-y-2.5 max-h-60 overflow-y-auto">
                  {notes.map((note) => (
                    <div key={note.id} className={`p-3 rounded-xl border ${note.visibility === 'internal' ? 'bg-amber-500/5 border-amber-500/20' : 'bg-white/5 border-white/10'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-bold text-gray-300">{note.author || 'Staff'}</span>
                        <div className="flex items-center gap-1.5">
                          {note.visibility === 'internal' ? (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 flex items-center gap-0.5"><EyeOff className="w-2.5 h-2.5" />Interne</span>
                          ) : (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" />Public</span>
                          )}
                          <span className="text-[9px] text-gray-600">{new Date(note.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                          <button onClick={() => deleteNote(note.id)} className="w-5 h-5 rounded-full bg-red-500/10 text-red-400 hover:bg-red-500/20 flex items-center justify-center"><Trash2 className="w-2.5 h-2.5" /></button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-200 whitespace-pre-wrap">{note.text}</p>
                      {note.attachments && note.attachments.length > 0 && (
                        <div className="mt-2 space-y-1.5">
                          {note.attachments.filter(a => (a.content_type || '').startsWith('image/')).length > 0 && (
                            <div className="grid grid-cols-4 gap-1.5">
                              {note.attachments.filter(a => (a.content_type || '').startsWith('image/')).map((att, i) => {
                                const attUrl = `${API_URL}/api/repair-notes/${note.id}/attachments/${att.id || att.file_id}${token ? `?token=${token}` : ''}`;
                                return (
                                  <a key={att.id || i} href={attUrl} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden border border-white/10 hover:border-cyan-500/50">
                                    <img src={attUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                                  </a>
                                );
                              })}
                            </div>
                          )}
                          {note.attachments.filter(a => (a.content_type || '').startsWith('video/')).map((att, i) => {
                            const attUrl = `${API_URL}/api/repair-notes/${note.id}/attachments/${att.id || att.file_id}${token ? `?token=${token}` : ''}`;
                            return (
                              <video key={att.id || i} controls preload="metadata" className="w-full max-h-40 rounded-lg border border-white/10 bg-black">
                                <source src={attUrl} type={att.content_type} />
                              </video>
                            );
                          })}
                          {note.attachments.filter(a => !(a.content_type || '').startsWith('image/') && !(a.content_type || '').startsWith('video/')).map((att, i) => {
                            const attUrl = `${API_URL}/api/repair-notes/${note.id}/attachments/${att.id || att.file_id}${token ? `?token=${token}` : ''}`;
                            return (
                              <a key={att.id || i} href={attUrl} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 hover:underline flex items-center gap-1">
                                <FileText className="w-3 h-3" />{att.filename}
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default B2BRepairDetailModal;
